import { useEffect, useState } from "react";
import feesAndPaymentApis, { InvoiceStats } from "../../api/feesAndPaymentApis";
import DataTable, { Column } from "../../components/DataTable";
import { InvoiceRow } from "../../components/modal/CreateInvoice";
import { Invoice } from "../../types/feesAndPaymentType";
import { fetchInvoices } from "../../features/feesAndPayment/FeesAndPaymentSlice";
import { useSelector } from "react-redux";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import Tag from "../../components/Tags";

interface Props {
  invoice: Invoice;
}

export default function InvoiceItemsList({ invoice }: Props) {
  const rows: InvoiceRow[] = invoice.invoiceItems.map((item, index) => ({
    id: String(index + 1),
    itemId: item.itemId,
    itemName: item.itemTitle,
    amount: item.baseAmount.toString(),
    discount: item.discount.toString(),
    discountType: item.discountType === "PERCENTAGE" ? "%" : "₹",
    discountRemark: item.discountRemarks ?? "",
    gst: `${item.tax}%`,
  }));

  const dispatch = useDispatch<AppDispatch>();
  const { invoices, page, size } = useSelector(
    (state: RootState) => state.fees,
  );

  const branchCode = useSelector(
    (state: RootState) => state.access.schoolBranch,
  );

  const { confirm, modal } = useConfirmToast();

  const handleDelete = async (invoiceId: number) => {
    const confirmed = await confirm(
      "Are you sure you want to delete this invoice?",
    );

    if (!confirmed) return;

    try {
      await feesAndPaymentApis.deleteInvoiceItem(invoiceId);
      toast.success("Invoice deleted successfully");
      dispatch(
        fetchInvoices({
          branchCode: branchCode.schoolBranchCode,
          schoolCode: branchCode.schoolCode,
          page,
          size,
        }),
      );
    } catch (error) {
      // error already handled in API (toast)
    }
  };

  if (!invoice.invoiceItems || invoice.invoiceItems.length === 0) {
    return null;
  }

  const columns: Column<InvoiceRow>[] = [
    { label: "SL NO.", accessor: "itemId" },
    { label: "Particular", accessor: "itemName" },
    { label: "Amount", accessor: "amount" },
    {
      label: "Discount",
      accessor: "discount",
      render: (row) => (
        <span>
          {row.discount}
          {row.discountType}
        </span>
      ),
    },
    {
      label: "Discount Remark",
      accessor: "discountRemark",
      render: (item) => (
        <p className="text-[13px]">{item.discountRemark || "---"}</p>
      ),
    },
    { label: "GST", accessor: "gst" },
    {
      label: "Item Total",
      accessor: "totalAmount",
      render: (row) => {
        const amount = Number(row.amount || 0);
        const discount = Number(row.discount || 0);
        const gst = Number(row.gst.replace("%", "") || 0);

        let discounted = amount;
        if (row.discountType === "%") {
          discounted -= (amount * discount) / 100;
        } else {
          discounted -= discount;
        }

        const total = discounted + (discounted * gst) / 100;
        return <span>₹{total.toFixed(2)}</span>;
      },
    },
    {
      label: "Action",
      accessor: "action",
      render: (item) => (
        <div className="flex gap-2">
          <Tag
            text="Delete"
            color="danger"
            onClick={() => handleDelete(item.itemId)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {modal}
      <DataTable columns={columns} data={rows} />
    </>
  );
}
